import { Linkable } from '@/components/blocks/Linkable';
import { PasswordInput } from '@/components/blocks/PasswordInput';
import { UserLogon } from '@/data/Content/Login';
import { useLocalization } from '@/data/Localization';
import { REG_EX } from '@/utils/address';
import { useForm } from '@/utils/useForm';
import { Stack } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import {
	SingleLoginFormEmailLabelSX,
	SingleLoginFormForgotPasswordBottomSX,
	SingleLoginFormForgotPasswordSX,
} from '@/components/blocks/SingleLoginForm/style';
import { DpBaseButton } from '@/components/blocks/DpBaseButton';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { addressFormFieldsSX } from '@/components/blocks/AddressForm/styles/fields';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { passwordValidation } from '@/utils/passwordValidation';

const errorsFormFields = {
	email: {
		empty: {
			errorMsg: 'Campo obligatorio',
		},
		formatIncorrect: {
			errorMsg: 'Por favor ingresa un número de teléfono válido',
		},
	},
	logonPassword: {
		empty: {
			errorMsg: 'Campo obligatorio',
		},
		rules: {
			errorMsg: 'Mínimo 1 letra, 1 número y 6 caractéres',
		},
	},
};

type Props = {
	signInButtonLabel?: string;
	forgotPasswordPosition?: 'form' | 'bottom' | 'line';
	onSubmit: (value: UserLogon) => void;
};

export const SingleLoginForm: FC<Props> = ({
	signInButtonLabel,
	forgotPasswordPosition,
	onSubmit,
}) => {
	const RouteLocal = useLocalization('Routes');
	const signInNLS = useLocalization('SignIn');
	const router = useNextRouter();

	const initLoginValues = useMemo<UserLogon>(
		() => ({
			email: (router.query.logonId as string) || '',
			logonId: (router.query.logonId as string) || '',
			logonPassword: '',
			rememberMe: false,
		}),
		[router.query]
	);

	const {
		handleInputChange,
		values: loginValues,
		formRef,
		handleSubmit,
		submitting,
	} = useForm(initLoginValues);
	const [errorEmail, setErrorEmail] = useState<any>({ error: false, errorMsg: '' });
	const [errorLogonPassword, setErrorLogonPassword] = useState<any>({ error: false, errorMsg: '' });

	const handleOnBlur = (_event: React.FocusEvent<HTMLInputElement>) => {
		const name = _event.target.name;
		const value = _event.target.value;

		if (name === 'logonPassword') {
			if (value.trim() === '') {
				setErrorLogonPassword({
					error: true,
					errorMsg: errorsFormFields[name].empty.errorMsg,
				});
			} else {
				setErrorLogonPassword({
					error: false,
					errorMsg: '',
				});
			}
		}

		if (name === 'email') {
			if (value.trim() === '') {
				setErrorEmail({
					error: true,
					errorMsg: errorsFormFields[name].empty.errorMsg,
				});
			} else {
				setErrorEmail({
					error: false,
					errorMsg: '',
				});
			}
		}
	};

	const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.value.length <= 10) {
			handleInputChange(event);
		}
	};

	const handleWrapperSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event?.preventDefault();

		if (loginValues.email?.length !== 10) {
			setErrorEmail({
				error: true,
				errorMsg:
					loginValues.email?.trim() !== ''
						? errorsFormFields.email.formatIncorrect.errorMsg
						: errorsFormFields.email.empty.errorMsg,
			});
			return;
		}

		const passwordValidationResult = passwordValidation(loginValues.logonPassword);

		const isPasswordValid =
			passwordValidationResult.Length &&
			passwordValidationResult.Number &&
			passwordValidationResult.Mayus;

		if (!isPasswordValid) {
			setErrorLogonPassword({
				error: true,
				errorMsg:
					loginValues.email?.trim() !== ''
						? errorsFormFields.logonPassword.rules.errorMsg
						: errorsFormFields.logonPassword.empty.errorMsg,
			});
			return;
		}

		handleSubmit(onSubmit)(event);
	};

	return (
		<Stack width="100%" component="form" onSubmit={handleWrapperSubmit} noValidate ref={formRef}>
			<DpOutlinedInput
				sx={{
					...addressFormFieldsSX,
					'.MuiOutlinedInput-input': {
						padding: '14px 14px',
						fontSize: 'var(--font-size-body)',
						height: '20px',
					},
					mb: '22px',
				}}
				type="number"
				variant="outlined"
				margin="normal"
				fullWidth
				required
				name="email"
				autoComplete="phone"
				id="email-sign-in-submit"
				label="Número de teléfono"
				onBlur={handleOnBlur}
				error={errorEmail.error}
				helperText={errorEmail.errorMsg}
				value={loginValues.email}
				onChange={handlePhoneChange}
				InputLabelProps={{
					sx: SingleLoginFormEmailLabelSX,
				}}
				inputProps={{
					maxLength: 10,
				}}
				FormHelperTextProps={{
					sx: { ml: '3px' },
				}}
			/>

			<PasswordInput
				sx={addressFormFieldsSX}
				required
				name="logonPassword"
				label="Contraseña"
				value={loginValues.logonPassword}
				// error={error.logonPassword}
				inputProps={{
					maxLength: 40,
					pattern: REG_EX.PASSWORD.source,
				}}
				onBlur={handleOnBlur}
				error={errorLogonPassword.error}
				helperText={errorLogonPassword.errorMsg}
				onChange={handleInputChange}
			/>

			{forgotPasswordPosition === 'form' ? (
				<Linkable
					href={`/${RouteLocal.ForgotPassword.route.t()}`}
					type="inline"
					sx={SingleLoginFormForgotPasswordSX}
					data-testid={RouteLocal.ForgotPassword.route.t()}
					id={RouteLocal.ForgotPassword.route.t()}
				>
					{signInNLS.ForgotPassword.t()}
				</Linkable>
			) : null}

			<DpBaseButton
				sx={{ mt: '20px' }}
				id="button-sign-in-submit"
				type="submit"
				disabled={submitting}
			>
				{signInButtonLabel ?? 'Iniciar sesión'}
			</DpBaseButton>

			{forgotPasswordPosition === 'bottom' || forgotPasswordPosition === 'line' ? (
				<Stack direction="row" justifyContent="center">
					<Linkable
						href={`/${RouteLocal.ForgotPassword.route.t()}`}
						type="inline"
						sx={SingleLoginFormForgotPasswordBottomSX}
						data-testid={RouteLocal.ForgotPassword.route.t()}
						id={RouteLocal.ForgotPassword.route.t()}
					>
						{signInNLS.ForgotPassword.t()}
					</Linkable>
				</Stack>
			) : null}
		</Stack>
	);
};
